import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Field, FieldRow } from "@/components/settings/settings-kit";
import { PLACEMENT_LABEL } from "@/components/content-programs/media-tile";
import {
  ASPECT_RATIO_RECOMMENDED,
  DEFAULT_CONTENT_PROGRAM_CONFIG,
  DEFAULT_IMAGE_MIMES,
  DEFAULT_VIDEO_MIMES,
  META_ASPECT_RATIOS,
  aspectRatioToClass,
  type ContentProgramConfig,
  type MetaAspectRatio,
  type MetaMediaType,
} from "@/config/contentProgramUpload";
import { cn } from "@/lib/utils";

type Props = {
  value: ContentProgramConfig;
  onChange: (next: ContentProgramConfig) => void;
};

const IMAGE_MIME_OPTIONS = [
  { mime: "image/jpeg", label: "JPEG" },
  { mime: "image/png", label: "PNG" },
  { mime: "image/webp", label: "WebP" },
  { mime: "image/gif", label: "GIF" },
];

const VIDEO_MIME_OPTIONS = [
  { mime: "video/mp4", label: "MP4" },
  { mime: "video/quicktime", label: "MOV" },
];

type Mode = "image" | "video" | "both";

function mediaTypeMode(types: MetaMediaType[]): Mode {
  const hasImage = types.includes("IMAGE");
  const hasVideo = types.includes("VIDEO");
  if (hasImage && hasVideo) return "both";
  if (hasVideo) return "video";
  return "image";
}

export function createDefaultContentConfig(): ContentProgramConfig {
  return { ...DEFAULT_CONTENT_PROGRAM_CONFIG };
}

/** Number field that stores undefined when cleared. */
function numberOr(value: string, parse: (s: string) => number) {
  const n = parse(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

/**
 * What affiliates may upload: media type, the Meta placements (drawn at
 * their real proportions so 4:5 and 9:16 are told apart at a glance),
 * then limits and formats.
 */
export default function ContentConfigForm({ value, onChange }: Props) {
  const mode = mediaTypeMode(value.allowedMediaTypes);
  const allowsImage = value.allowedMediaTypes.includes("IMAGE");
  const allowsVideo = value.allowedMediaTypes.includes("VIDEO");

  const setMode = (next: Mode) => {
    const allowedMediaTypes: MetaMediaType[] =
      next === "image" ? ["IMAGE"] : next === "video" ? ["VIDEO"] : ["IMAGE", "VIDEO"];
    onChange({
      ...value,
      allowedMediaTypes,
      allowedImageMimes: value.allowedImageMimes ?? DEFAULT_IMAGE_MIMES,
      allowedVideoMimes: value.allowedVideoMimes ?? DEFAULT_VIDEO_MIMES,
    });
  };

  const toggleRatio = (ratio: MetaAspectRatio) => {
    const set = new Set(value.allowedAspectRatios);
    if (set.has(ratio)) {
      if (set.size <= 1) return;
      set.delete(ratio);
    } else {
      set.add(ratio);
    }
    const allowedAspectRatios = META_ASPECT_RATIOS.filter((r) => set.has(r));
    const only = allowedAspectRatios.length === 1 ? ASPECT_RATIO_RECOMMENDED[allowedAspectRatios[0]] : null;
    onChange({
      ...value,
      allowedAspectRatios,
      ...(only ? { minResolutionWidth: only.width, minResolutionHeight: only.height } : {}),
    });
  };

  const toggleMime = (kind: "image" | "video", mime: string) => {
    const key = kind === "image" ? "allowedImageMimes" : "allowedVideoMimes";
    const current = value[key] ?? (kind === "image" ? DEFAULT_IMAGE_MIMES : DEFAULT_VIDEO_MIMES);
    const set = new Set(current);
    if (set.has(mime)) {
      if (set.size <= 1) return;
      set.delete(mime);
    } else {
      set.add(mime);
    }
    onChange({ ...value, [key]: [...set] });
  };

  return (
    <div className="space-y-5">
      <Field label="Media">
        <RadioGroup value={mode} onValueChange={(v) => setMode(v as Mode)} className="flex flex-wrap gap-x-6 gap-y-2">
          {(
            [
              ["image", "Images"],
              ["video", "Videos"],
              ["both", "Images and videos"],
            ] as const
          ).map(([key, label]) => (
            <div key={key} className="flex items-center gap-2">
              <RadioGroupItem value={key} id={`media-${key}`} />
              <Label htmlFor={`media-${key}`} className="font-normal text-foreground">
                {label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </Field>

      <Field label="Placements" help="Affiliates can only upload media in these proportions">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {META_ASPECT_RATIOS.map((ratio) => {
            const rec = ASPECT_RATIO_RECOMMENDED[ratio];
            const selected = value.allowedAspectRatios.includes(ratio);
            return (
              <button
                key={ratio}
                type="button"
                aria-pressed={selected}
                onClick={() => toggleRatio(ratio)}
                className={cn(
                  "flex min-w-0 flex-col items-stretch gap-3 rounded-md border px-3 py-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  selected ? "border-brand-600 ring-1 ring-brand-600" : "border-border hover:bg-muted/60"
                )}
              >
                <span className="flex h-10 items-end self-start">
                  <span
                    aria-hidden
                    className={cn(
                      "block h-10 rounded-[3px]",
                      aspectRatioToClass(ratio),
                      selected ? "bg-brand-600" : "bg-muted-foreground/25"
                    )}
                  />
                </span>
                <span className="block min-w-0">
                  <span className="block text-sm font-medium tabular-nums text-foreground">{ratio}</span>
                  <span className="block truncate text-xs text-muted-foreground" title={PLACEMENT_LABEL[ratio]}>
                    {PLACEMENT_LABEL[ratio]}
                  </span>
                  <span className="block text-xs tabular-nums text-muted-foreground">
                    {rec.width}×{rec.height}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </Field>

      <FieldRow>
        {allowsImage && (
          <Field label="Max image size" htmlFor="cfg-image-mb" help="Megabytes">
            <Input
              id="cfg-image-mb"
              type="number"
              inputMode="decimal"
              min={0.1}
              step={0.1}
              className="tabular-nums"
              value={value.maxImageSizeMb ?? ""}
              onChange={(e) => onChange({ ...value, maxImageSizeMb: numberOr(e.target.value, Number.parseFloat) })}
            />
          </Field>
        )}
        {allowsVideo && (
          <Field label="Max video size" htmlFor="cfg-video-mb" help="Megabytes">
            <Input
              id="cfg-video-mb"
              type="number"
              inputMode="decimal"
              min={0.1}
              step={0.1}
              className="tabular-nums"
              value={value.maxVideoSizeMb ?? ""}
              onChange={(e) => onChange({ ...value, maxVideoSizeMb: numberOr(e.target.value, Number.parseFloat) })}
            />
          </Field>
        )}
        {allowsVideo && (
          <Field label="Max video length" htmlFor="cfg-video-seconds" help="Seconds">
            <Input
              id="cfg-video-seconds"
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              className="tabular-nums"
              value={value.maxVideoDurationSeconds ?? ""}
              onChange={(e) =>
                onChange({
                  ...value,
                  maxVideoDurationSeconds: numberOr(e.target.value, (s) => Number.parseInt(s, 10)),
                })
              }
            />
          </Field>
        )}
      </FieldRow>

      <FieldRow>
        <Field label="Min width" htmlFor="cfg-min-w" help="Pixels, optional">
          <Input
            id="cfg-min-w"
            type="number"
            inputMode="numeric"
            min={1}
            className="tabular-nums"
            value={value.minResolutionWidth ?? ""}
            onChange={(e) =>
              onChange({ ...value, minResolutionWidth: numberOr(e.target.value, (s) => Number.parseInt(s, 10)) })
            }
          />
        </Field>
        <Field label="Min height" htmlFor="cfg-min-h" help="Pixels, optional">
          <Input
            id="cfg-min-h"
            type="number"
            inputMode="numeric"
            min={1}
            className="tabular-nums"
            value={value.minResolutionHeight ?? ""}
            onChange={(e) =>
              onChange({ ...value, minResolutionHeight: numberOr(e.target.value, (s) => Number.parseInt(s, 10)) })
            }
          />
        </Field>
      </FieldRow>

      {allowsImage && (
        <Field label="Image formats">
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {IMAGE_MIME_OPTIONS.map(({ mime, label }) => {
              const id = `mime-${label}`;
              return (
                <div key={mime} className="flex items-center gap-2">
                  <Checkbox
                    id={id}
                    checked={(value.allowedImageMimes ?? DEFAULT_IMAGE_MIMES).includes(mime)}
                    onCheckedChange={() => toggleMime("image", mime)}
                  />
                  <Label htmlFor={id} className="font-normal text-foreground">
                    {label}
                  </Label>
                </div>
              );
            })}
          </div>
        </Field>
      )}

      {allowsVideo && (
        <Field label="Video formats">
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {VIDEO_MIME_OPTIONS.map(({ mime, label }) => {
              const id = `mime-${label}`;
              return (
                <div key={mime} className="flex items-center gap-2">
                  <Checkbox
                    id={id}
                    checked={(value.allowedVideoMimes ?? DEFAULT_VIDEO_MIMES).includes(mime)}
                    onCheckedChange={() => toggleMime("video", mime)}
                  />
                  <Label htmlFor={id} className="font-normal text-foreground">
                    {label}
                  </Label>
                </div>
              );
            })}
          </div>
        </Field>
      )}
    </div>
  );
}
